import React from 'react';
import SheetRefDropdown from './inputs/SheetRefDropdown';
import SheetTextInput from './inputs/SheetTextInput';
import MilestoneDateCell from './MilestoneDateCell';
import { toInputValue } from './documentStateHelpers';
import { normalizeRefId } from './resolveVariableField';
import './inputs/SheetInputs.scss';

const toDateInputValue = value => {
  if (value === undefined || value === null || value === '') return '';
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }

  const asString = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(asString)) return asString;

  const parsed = new Date(asString);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }

  return '';
};

/**
 * Controlled editor for a {{variable}} cell.
 */
const VariableCell = ({
  binding,
  value,
  onChange,
  disabled = false,
  textAlign = 'left',
  title,
  inline = false,
  appChoices,
  buyerEmail
}) => {
  const hint = binding?.hint || binding?.statePath?.join(' | ') || title;
  const tooltip = title ? `${hint} (${title})` : hint;
  const wrapperClass = inline ? 'dochub-variable-cell is-inline' : 'dochub-variable-cell';

  if (binding?.type === 'milestone') {
    return (
      <MilestoneDateCell
        config={binding.milestoneConfig}
        milestone={value && typeof value === 'object' ? value : null}
        buyerEmail={buyerEmail}
        appChoices={appChoices}
        disabled={disabled}
        textAlign={textAlign}
        title={title}
        inline={inline}
        onChange={onChange}
      />
    );
  }

  if (binding?.type === 'ref') {
    const refId = normalizeRefId(value);
    const { options = [], optionValues = [] } = binding;

    // Ensure current value appears even if not in filtered list (e.g. model after manufacturer change)
    let displayOptions = options;
    let displayValues = optionValues;
    if (refId && !optionValues.includes(refId)) {
      const populated =
        value && typeof value === 'object'
          ? binding.displayLeaf
            ? value[binding.displayLeaf] || 'N/A'
            : binding.refConfig?.labelKey
              ? value[binding.refConfig.labelKey] || value.name || value.location
              : value.name
          : refId;
      displayOptions = [String(populated || refId), ...options];
      displayValues = [refId, ...optionValues];
    }

    return (
      <div className={wrapperClass}>
        <SheetRefDropdown
          value={refId}
          options={displayOptions}
          optionValues={displayValues}
          textAlign={textAlign}
          disabled={disabled}
          title={tooltip}
          placeholder={hint}
          onChange={onChange}
        />
      </div>
    );
  }

  const inputType = binding?.inputType || (binding?.type === 'number' ? 'number' : 'text');
  const displayValue = inputType === 'date' ? toDateInputValue(value) : toInputValue(value);
  const isObjectValue = value !== null && typeof value === 'object';

  return (
    <div className={wrapperClass}>
      <SheetTextInput
        type={inputType}
        value={displayValue}
        textAlign={textAlign}
        inline={inline}
        disabled={disabled || isObjectValue}
        title={tooltip}
        placeholder={isObjectValue ? '(complex value)' : hint}
        aria-label={hint}
        onChange={onChange}
      />
    </div>
  );
};

export default VariableCell;
